<script setup>

  import { API, Cache } from 'aws-amplify';
  import { CarouselSlidesByLangByDate } from '~/graphql/queries';

  // nyelvi es routing beallitasok
  const localePath = useLocalePath();
  const { t, locale } = useI18n();
  const route = useRoute();

  // pinia storage
  import { usePageStore } from '@/store/page';
  import { storeToRefs } from 'pinia';

  // page setup:
  const pageStore = usePageStore();
  const { title } = storeToRefs(pageStore);

  definePageMeta({ 
    layout: 'home',
  });

  // In-page almenü:
  // TODO: hozzáadni majd a slides részhez a lekérdezéseket
  const swipers = [
    {
      hash: 'newly-published',
      title: 'Newly Published',
      icon: 'fire',
      slides: [],
    },
    {
      hash: 'bestsellers-of-the-month',
      title: 'Bestsellers of the Month',
      icon: 'trophy',
      slides: [],
    },
    {
      hash: 'upcoming-releases',
      title: 'Upcoming Releases',
      icon: 'stopwatch',
      slides: [],
    },
    {
      hash: 'staff-recommendations',
      title: 'Staff Recommendations',
      icon: 'check',
      slides: [],
    },
  ];

  // változó definíciók
  let loading = ref(true)
  let carouselSlides = ref([])

  // foldali carousel slider tartalmanak lekerdezese
  const fetchCarousel = async () => {
    loading.value = true
    try {
      // AWS Amplify cache teszt --->
      const cachedData = Cache.getItem('fetchCarousel' + locale.value);
      if(cachedData) {
        return cachedData;
      }
      const dataholder = await API.graphql({
        query: CarouselSlidesByLangByDate,
        variables: {
          lang: locale.value,
          sortDirection: 'DESC',
        },
        authMode: 'AWS_IAM',
      });
      // AWS Amplify cache teszt --->
      const now = new Date();
      const expiration = new Date(now.getTime() + 5 * 60 * 1000); // Jelen idő + 5 perc
      Cache.setItem('fetchCarousel' + locale.value, dataholder.data.CarouselSlidesByLangByDate.items, { expires: expiration.getTime() });
      // <--- eddig
      return dataholder.data.CarouselSlidesByLangByDate.items;
    } catch (error) {
      console.log(error)
      // $bugsnag.notify(new Error(error))
      return []
    } finally {
      loading.value = false
    }
  };

  // carousel lekérdezése
  carouselSlides = await fetchCarousel();

</script>

<template>
  <div id="topd" class="max-w-screen">
    <Head>
      <Title>The best foreign-language bookstore in Budapest</Title>
    </Head>
    <NavigationCenteredSearch />
    <CarouselCentered v-if="carouselSlides" :slides="carouselSlides" class="shadow-inner" />

    <main id="indexpage">

      <!-- in-page nav bar -->
      <nav
        class="hidden md:flex lg:flex xl:flex flex-no-wrap shadow-inner mx-auto container"
      >
        <a
          v-if="swipers && swipers.length"
          v-for="swiper in swipers"
          :key="swiper.id"
          class="flex-1 md:m-1 lg:m-4 xl:m-4 px-6 md:py-0 lg:py-2 xl:py-2 text-center bg-navbgsecondary-dark rounded-md transition-colors duration-300 hover:text-txt-light"
          :href="`#${swiper.hash}`"
        >
          <p class="text-2xl mx-2">
            <font-awesome-icon :icon="['fas', `${swiper.icon}`]" />
          </p>
          <p class="">{{ $t(swiper.title) }}</p>
          <p class="text-xs">
            <font-awesome-icon :icon="['fas', 'angle-down']" />
          </p>
        </a>
        <NuxtLink
          :to="localePath('/book-club')"
          class="flex-1 md:m-1 lg:m-4 xl:m-4 px-6 md:py-0 lg:py-2 xl:py-2 text-center bg-navbgsecondary-dark rounded-md transition-colors duration-300 hover:text-txt-light"
        >
          <p class="text-2xl mx-2">
            <font-awesome-icon :icon="['fas', 'user-friends']" />
          </p>
          <p class="">{{ $t('Bestsellers Book Club') }}</p>
          <p class="text-xs">
            <font-awesome-icon :icon="['fas', 'angle-right']" />
          </p>
        </NuxtLink>
      </nav>

      <!-- Product swiper konténerek innen -->
      <LazyProductSwiperContainer 
        v-if="!loading"
        :key="'newly-published'"
        :productcategorytitle="$t('Newly Published')" 
        :title="$t('Newly Published')" 
        :icon="'fire'"
        :htmltagid="'newly-published'" 
        :path="'/newly-published'"
        :fromproductcategoryid="'4380834e-c1f2-4bc9-8027-19228930b43c'"
      />
      <div v-else class="sm:mx-auto md:mx-auto lg:mx-auto xl:mx-auto lg:container xl:container pt-4">
        <UiLoadSpinner v-show="loading" />
      </div>
      <LazyProductSwiperContainer 
        v-if="!loading"
        :key="'bestsellers-of-the-month'"
        :productcategorytitle="$t('Bestsellers of the Month')" 
        :title="$t('Bestsellers of the Month')" 
        :icon="'trophy'"
        :htmltagid="'bestsellers-of-the-month'" 
        :path="'/bestsellers-of-the-month'"
        :fromproductcategoryid="'5eaa2de1-076f-4a31-908e-3893a005beba'"
      />
      <LazyProductSwiperContainer 
        v-if="!loading"
        :key="'upcoming-releases'"
        :productcategorytitle="$t('Upcoming Releases')" 
        :title="$t('Upcoming Releases')" 
        :icon="'stopwatch'"
        :htmltagid="'upcoming-releases'" 
        :path="'/upcoming-releases'"
        :fromproductcategoryid="'5c151e72-936a-47dd-ac6c-b41dd866cd9f'"
      />
      <LazyProductSwiperContainer 
        v-if="!loading"
        :key="'staff-recommendations'"
        :productcategorytitle="$t('Staff Recommendations')" 
        :title="$t('Staff Recommendations')" 
        :icon="'check'"
        :htmltagid="'staff-recommendations'" 
        :path="'/staff-recommendations'"
        :fromproductcategoryid="'5272a2da-8043-4e73-b8c5-440f760fee86'"
      />
    </main>
  </div>
</template>
